import React, { useState } from 'react';

import { notify } from '@NOTIFICATION/Notificator';

import { EntityTable, EntityFormModal } from '@COMPONENTS/SHARED/EntityManagement';

import {
    useGetAllPropertiesAdmin,
    useCreateProperty,
    useUpdateProperty,
    useDeleteProperty,
} from '@SERVICES';
import { Property } from '@INTERFACES/api/properties';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

function ContractTypesTab() {
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [editingProperty, setEditingProperty] = useState<Property | null>(null);

    const { data: properties = [], isLoading, refetch } = useGetAllPropertiesAdmin();
    const [createProperty, { isLoading: isCreating }] = useCreateProperty();
    const [updateProperty, { isLoading: isUpdating }] = useUpdateProperty();
    const [deleteProperty] = useDeleteProperty();

    const columns = [
        {
            key: 'id' as keyof Property,
            header: 'ID',
        },
        {
            key: 'name' as keyof Property,
            header: 'Name',
        },
    ];

    const formFields = [
        {
            key: 'name',
            label: 'Name',
            type: 'text' as const,
            required: true,
            placeholder: 'Enter property type name',
        },
    ];

    const handleCreate = () => {
        setEditingProperty(null);
        setIsFormModalOpen(true);
    };

    const handleEdit = (property: Property) => {
        setEditingProperty(property);
        setIsFormModalOpen(true);
    };

    const handleDelete = async (property: Property) => {
        if (window.confirm(`Are you sure you want to delete "${property.name}"?`)) {
            try {
                await deleteProperty(property.id).unwrap();
                notify.success('Property type deleted successfully!');
                refetch();
            } catch (error) {
                // Extract specific error message from API response
                let errorMessage = 'Failed to delete property type!';
                
                if (error && typeof error === 'object' && 'status' in error) {
                    const fetchError = error as FetchBaseQueryError;
                    if (typeof fetchError.status === 'number' && fetchError.data) {
                        // Try to extract the message from the API response
                        const errorData = fetchError.data as any;
                        if (errorData && typeof errorData === 'object' && errorData.message) {
                            errorMessage = errorData.message;
                        }
                    }
                }
                
                notify.error(errorMessage);
            }
        }
    };

    const handleFormSubmit = async (formData: any) => {
        try {
            if (editingProperty) {
                await updateProperty({ id: editingProperty.id, ...formData }).unwrap();
                notify.success('Property type updated successfully!');
            } else {
                await createProperty(formData).unwrap();
                notify.success('Property type created successfully!');
            }
            setIsFormModalOpen(false);
            setEditingProperty(null);
            refetch();
        } catch (error) {
            notify.error(`Failed to ${editingProperty ? 'update' : 'create'} property type!`);
        }
    };

    const handleFormClose = () => {
        setIsFormModalOpen(false);
        setEditingProperty(null);
    };

    return (
        <div style={{ padding: '24px' }}>
            <EntityTable
                data={properties}
                columns={columns}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onCreate={handleCreate}
                isLoading={isLoading}
                entityName="Property Type"
            />

            <EntityFormModal
                isOpen={isFormModalOpen}
                onClose={handleFormClose}
                onSubmit={handleFormSubmit}
                title={editingProperty ? 'Edit Property Type' : 'Create Property Type'}
                fields={formFields}
                initialData={editingProperty}
                isLoading={isCreating || isUpdating}
            />
        </div>
    );
}

export default ContractTypesTab; 